/* eslint-disable react/react-in-jsx-scope */
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* */
import React, { Suspense, useEffect, useState } from 'react';
import Framework from '@greenville/framework';
import {
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import { ensureTdxAcsDevToStagingRewrite } from '../../../../config/tdxAcsHost';
import { ensurePiSessionForStandalone } from '../../../../bootstrap/piSessionStandalone';
import * as studioConstants from '../../constants/StudioTextConstants';
import { configTypeMappings } from '../../utils';
import TokenService from '../../services/TokenService';
import useTestBedLauncherStyles from './TestbedLauncherStyles';

const standaloneLearnerContextId = '69aa1d441990154a1eb73c76';
const standaloneSourceId = 'xlplus-mod-stg';
// Loaded at runtime via webpack Module Federation. The `guidedStudyChatMFE`
// remote is registered in `config-overrides.js`. Its `remoteEntry.js` is
// served from the URL configured by `REACT_APP_GUIDED_STUDY_CHAT_MFE_URL`.
const GuidedStudyChat = React.lazy(() =>
  // eslint-disable-next-line import/no-unresolved
  import('guidedStudyChatMFE/ChatApplication').then((mod) => ({
    default: mod.ChatApplication || mod.default
  }))
);

/** Default assignment shape — any `extraConfig` key that matches these keys is merged into `assignment`. */
const getDefaultAssignmentTemplate = (decodedData) => ({
  id: 'a9b6d35a-fa7c-4e9a-b8a7-6d4866dfc79f',
  assessmentId: 'a9b6d35a-fa7c-4e9a-b8a7-6d4866dfc79f',
  courseId: decodedData?.courseId || '69c2c1b8098ba953a263a855',
  userName: 'S T',
  userPiId: '96f269fd96a24b4c96421d72c6f1082e',
  assessmentType: 'TopicFocused',
  title: 'Mock Guided Study Assignment',
  items: [],
  maxPoints: 0,
  pointsEarned: 0,
  dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
});

/** Default Berlin run-graph payload shape (minus graph routing) — matching `extraConfig` keys merge into this object. */
const BASE_AGENTIC_RUN_GRAPH_DEFAULTS = {
  bookCodeId: '100076',
  assessmentId: '',
  studentId: '',
  sourceId: standaloneSourceId,
  learningContext: standaloneLearnerContextId,
  objectiveId: 'urn:sb:book:bzpc7ePLU2:chapter:7:section:2:objective:2',
  assignmentId: '',
  questionMetadata: false,
  topN: 1,
  skillIds: ['100076_7_2_2']
};

/**
 * Routes JSON config: exact key match → assignment or agentic payload; known aliases; graph keys for resolution.
 * Any other key is left for top-level ChatApplication spread.
 */
const partitionExtraConfig = (extraConfig, decodedData) => {
  const assignmentTemplate = getDefaultAssignmentTemplate(decodedData);
  const assignmentKeySet = new Set(Object.keys(assignmentTemplate));
  const agenticKeySet = new Set(Object.keys(BASE_AGENTIC_RUN_GRAPH_DEFAULTS));

  const consumed = new Set();
  const assignmentPatch = {};
  const agenticPatch = {};

  Object.entries(extraConfig).forEach(([key, value]) => {
    if (assignmentKeySet.has(key)) {
      assignmentPatch[key] = value;
      consumed.add(key);
    } else if (key === 'studentId') {
      assignmentPatch.userPiId = value;
      consumed.add(key);
    } else if (key === 'bookCode') {
      agenticPatch.bookCodeId = String(value);
      consumed.add(key);
    } else if (key === 'graphId' || key === 'version' || key === 'graphVersion') {
      consumed.add(key);
    } else if (agenticKeySet.has(key)) {
      agenticPatch[key] = value;
      consumed.add(key);
    }
  });

  return {
    assignmentTemplate,
    assignmentPatch,
    agenticPatch,
    restExtraConfig: _.omit(extraConfig, [...consumed])
  };
};

const buildMockModuleProps = (decodedData) => {
  const extraConfig = decodedData?.extraConfig || {};
  const { assignmentTemplate, assignmentPatch, agenticPatch, restExtraConfig } = partitionExtraConfig(
    extraConfig,
    decodedData
  );

  const resolvedGraphId = extraConfig.graphId || decodedData?.graphId || '1c07624d-f3d8-4a51-bd5b-2537a8395e84';
  const resolvedVersion = String(extraConfig.graphVersion ?? extraConfig.version ?? decodedData?.version ?? '1');

  const mockAssignment = {
    ...assignmentTemplate,
    ...assignmentPatch
  };

  const standaloneAgenticRunGraphPayload = {
    ...BASE_AGENTIC_RUN_GRAPH_DEFAULTS,
    assessmentId: mockAssignment.assessmentId,
    studentId: mockAssignment.userPiId,
    assignmentId: mockAssignment.assessmentId,
    ...agenticPatch,
    graphId: resolvedGraphId,
    graphVersion: resolvedVersion
  };

  const learnerContextId = standaloneAgenticRunGraphPayload.learningContext ?? standaloneLearnerContextId;
  const source = standaloneAgenticRunGraphPayload.sourceId ?? standaloneSourceId;

  return {
    assignment: mockAssignment,
    learnerContextId,
    source,
    assignmentName: mockAssignment.title,
    totalScore: mockAssignment.maxPoints,
    assessmentType: mockAssignment.assessmentType,
    agenticRunGraphPayload: standaloneAgenticRunGraphPayload,
    ...restExtraConfig
  };
};

const GuidedStudyTestbedLauncher = () => {
  const classes = useTestBedLauncherStyles();
  const location = useLocation();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [sysToSysToken, setSysToSysToken] = useState('');
  const [decodedData, setDecodedData] = useState(null);

  useEffect(() => {
    ensureTdxAcsDevToStagingRewrite();
  }, []);

  useEffect(() => {
    const token = utils.getToken();
    setUserToken(token || '');
  }, []);

  useEffect(() => {
    const getSysToSysToken = async () => {
      const isProd = decodedData?.berlinEnv === 'prod' || decodedData?.berlinEnv === 'ppe';
      const token = await TokenService.getSys2sysToken(isProd);
      if (!_.isEmpty(token)) {
        setSysToSysToken(token);
      }
    };

    if (decodedData) {
      getSysToSysToken();
    }
  }, [decodedData]);

  useEffect(() => {
    const tokenToUse = sysToSysToken || userToken;
    if (!_.isEmpty(tokenToUse)) {
      const hasPiSessionToken = ensurePiSessionForStandalone(tokenToUse, true);
      if (!hasPiSessionToken) return;
      setTimeout(() => {
        setIsPageLoaded(true);
      }, 500);
    }
  }, [sysToSysToken, userToken]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const encodedData = searchParams.get('configs');

    if (encodedData) {
      const decodedString = atob(encodedData);
      const decodedJsonString = decodeURIComponent(decodedString);
      const parsedData = JSON.parse(decodedJsonString);
      setDecodedData(parsedData);
    }
  }, [location.search]);

  const formattedValue = (value) => {
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value;
  };

  const handleCopyURL = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'success',
        message: 'URL copied to clipboard successfully!'
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'success',
        message: 'URL copied to clipboard successfully!'
      });
    }
  };

  if (!isPageLoaded || !decodedData) return null;

  const mockModuleProps = buildMockModuleProps(decodedData);

  return (
    <div className={classes.contentWrapper}>
      {/* <div className={classes.heading}>
        {studioConstants.WELCOME_TO_TESTBED}
        <Tooltip title={studioConstants.COPY_URL}>
          <Box display="flex" alignItems="center" onClick={handleCopyURL} className={classes.copyUrlBox}>
            <LinkIcon className={classes.copyUrlIcon} />
            <Typography variant="caption" className={classes.copyUrlText}>
              {studioConstants.SHARE_GRAPH}
            </Typography>
          </Box>
        </Tooltip>
      </div> */}

      <Suspense
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        }
      >
        <GuidedStudyChat {...mockModuleProps} />
      </Suspense>
    </div>
  );
};

export default observer(GuidedStudyTestbedLauncher);
